import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Check, AlertCircle, Award, FileText, Briefcase, GraduationCap, MapPin, Mail, Phone as PhoneIcon } from "lucide-react";
import { api, formatError } from "../../api";

/**
 * AI-powered resume insights for an HR-side application review.
 * Lets recruiter trigger Claude parsing on the candidate's resume and
 * see structured skills, experience and a 0-100 fit-score vs. the posting.
 */
export default function ResumeInsights({ appId, hasResume }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const parsed = data?.parsed;
  const fit = data?.fit;

  const load = async () => {
    setErr("");
    try {
      const r = await api.get(`/applications/${appId}/resume-insights`);
      setData(r.data);
    } catch (e) { setErr(formatError(e)); }
  };
  useEffect(() => { load(); }, [appId]);

  const parse = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/applications/${appId}/parse-resume`);
      setData(r.data);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  if (!hasResume) {
    return (
      <div data-testid="resume-insights" className="space-y-2">
        <div className="text-sm text-muted text-center py-6" data-testid="ai-no-resume">
          Candidate hasn&apos;t uploaded a resume yet — insights will appear once they do.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="resume-insights">
      <div className="rounded-xl border p-4 flex items-center justify-between gap-3"
        style={{ borderColor: "var(--rule)", background: "linear-gradient(135deg, rgba(0,108,255,0.04), rgba(153,51,255,0.06))" }}>
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold" style={{ color: "var(--signal)" }}>
            <Sparkles size={14} /> AI resume insights
          </div>
          <div className="text-[11px] text-muted mt-1">
            Powered by Claude · skills extracted + fit-score computed against the posting requirements.
          </div>
        </div>
        <button
          type="button"
          onClick={parse}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md text-white shadow-sm disabled:opacity-60"
          style={{ background: "linear-gradient(135deg, #006CFF, #9933FF)" }}
          data-testid="parse-resume-btn"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {busy ? "Reading…" : (parsed ? "Re-parse" : "Parse with AI")}
        </button>
      </div>

      {err && (
        <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2 flex items-center gap-2" data-testid="ai-err">
          <AlertCircle size={14} /> {err}
        </div>
      )}

      {!parsed && !busy && data?.status === "parsing" && (
        <div className="text-sm text-center py-6 text-muted inline-flex items-center justify-center gap-2 w-full" data-testid="ai-parsing">
          <Loader2 size={14} className="animate-spin" />
          Resume is being analysed in the background — this usually takes 5–10s. Click <b>Re-parse</b> to refresh.
        </div>
      )}

      {!parsed && !busy && (!data?.status || data?.status === "not_parsed") && (
        <div className="text-sm text-muted text-center py-4" data-testid="ai-empty">
          No analysis yet — click <b>Parse with AI</b> to extract skills, experience and fit-score.
        </div>
      )}

      {parsed && (
        <>
          {/* Fit gauge */}
          {fit?.score != null && (
            <div className="rounded-xl border p-4 text-center" style={{ borderColor: "var(--rule)" }} data-testid="ai-fit-score">
              <div className="text-xs uppercase tracking-wider text-muted">Fit Score</div>
              <div className="font-display font-black text-5xl mt-1"
                style={{ color: fit.score >= 75 ? "#16A34A" : fit.score >= 50 ? "#0040A8" : "#DC2626" }}>
                {fit.score}<span className="text-xl text-muted">/100</span>
              </div>
              <div className="text-[11px] text-muted mt-1">
                {fit.matched?.length || 0} of {(fit.matched?.length || 0) + (fit.missing?.length || 0)} required skills match · {(fit.experience_years ?? 0)} yrs experience
              </div>
              <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.06)" }}>
                <div className="h-full transition-[width] duration-700"
                  style={{
                    width: `${Math.min(100, fit.score)}%`,
                    background: fit.score >= 75 ? "linear-gradient(90deg,#16A34A,#15803D)" : fit.score >= 50 ? "linear-gradient(90deg,#006CFF,#0040A8)" : "linear-gradient(90deg,#DC2626,#991B1B)",
                  }} />
              </div>
            </div>
          )}

          {/* Summary */}
          {parsed.summary && (
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--rule)" }} data-testid="ai-summary">
              <div className="text-[11px] uppercase tracking-wider text-muted font-bold mb-1">AI Summary</div>
              <p className="text-sm leading-relaxed">{parsed.summary}</p>
            </div>
          )}

          {/* Matched / Missing skills */}
          {fit && (
            <div className="grid grid-cols-2 gap-3" data-testid="ai-skills-grid">
              <div className="rounded-xl border p-3" style={{ borderColor: "rgba(22,163,74,0.3)" }}>
                <div className="text-[11px] uppercase tracking-wider font-bold flex items-center gap-1 mb-2" style={{ color: "#16A34A" }}>
                  <Check size={12} /> Matched · {fit.matched?.length || 0}
                </div>
                <div className="flex flex-wrap gap-1">
                  {(fit.matched || []).map((s) => (
                    <span key={s} className="text-[11px] px-1.5 py-0.5 rounded font-medium" style={{ background: "rgba(22,163,74,0.10)", color: "#15803D" }}>{s}</span>
                  ))}
                  {(fit.matched?.length || 0) === 0 && <span className="text-xs text-muted">None matched</span>}
                </div>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: "rgba(220,38,38,0.25)" }}>
                <div className="text-[11px] uppercase tracking-wider font-bold flex items-center gap-1 mb-2" style={{ color: "#DC2626" }}>
                  <AlertCircle size={12} /> Missing · {fit.missing?.length || 0}
                </div>
                <div className="flex flex-wrap gap-1">
                  {(fit.missing || []).map((s) => (
                    <span key={s} className="text-[11px] px-1.5 py-0.5 rounded font-medium" style={{ background: "rgba(220,38,38,0.08)", color: "#991B1B" }}>{s}</span>
                  ))}
                  {(fit.missing?.length || 0) === 0 && <span className="text-xs text-muted">Nothing missing — strong match!</span>}
                </div>
              </div>
            </div>
          )}

          {/* Contact + meta */}
          <div className="rounded-xl border p-3 grid grid-cols-2 gap-2 text-xs" style={{ borderColor: "var(--rule)" }} data-testid="ai-contact">
            {parsed.contact_email && <div className="inline-flex items-center gap-1.5"><Mail size={12} className="text-muted" /> {parsed.contact_email}</div>}
            {parsed.contact_phone && <div className="inline-flex items-center gap-1.5"><PhoneIcon size={12} className="text-muted" /> {parsed.contact_phone}</div>}
            {parsed.location && <div className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-muted" /> {parsed.location}</div>}
            {parsed.current_role && <div className="inline-flex items-center gap-1.5"><Briefcase size={12} className="text-muted" /> {parsed.current_role}{parsed.current_company ? ` @ ${parsed.current_company}` : ""}</div>}
          </div>

          {/* Extra skills */}
          {fit?.extra?.length > 0 && (
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--rule)" }}>
              <div className="text-[11px] uppercase tracking-wider font-bold text-muted mb-2">Additional skills</div>
              <div className="flex flex-wrap gap-1">
                {fit.extra.slice(0, 16).map((s) => (
                  <span key={s} className="text-[11px] px-1.5 py-0.5 rounded font-mono" style={{ background: "rgba(0,0,0,0.04)" }}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Experience */}
          {parsed.experience?.length > 0 && (
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--rule)" }} data-testid="ai-experience">
              <div className="text-[11px] uppercase tracking-wider font-bold text-muted mb-2 flex items-center gap-1">
                <Briefcase size={12} /> Experience
              </div>
              <div className="space-y-2">
                {parsed.experience.slice(0, 5).map((x, i) => (
                  <div key={`exp-${i}-${(x.role || "") + (x.company || "")}`} className="text-sm">
                    <div className="font-semibold">{x.role || "—"} {x.company ? <span className="text-muted">· {x.company}</span> : null}</div>
                    <div className="text-[11px] text-muted">{x.start || "—"} → {x.end || "Present"}</div>
                    {x.summary && <div className="text-xs mt-0.5 text-muted">{x.summary}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {parsed.education?.length > 0 && (
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--rule)" }} data-testid="ai-education">
              <div className="text-[11px] uppercase tracking-wider font-bold text-muted mb-2 flex items-center gap-1">
                <GraduationCap size={12} /> Education
              </div>
              <div className="space-y-1.5">
                {parsed.education.slice(0, 4).map((x, i) => (
                  <div key={`edu-${i}-${(x.degree || "") + (x.institution || "")}`} className="text-sm">
                    <span className="font-semibold">{x.degree || "—"}</span>
                    {x.institution && <span className="text-muted"> · {x.institution}</span>}
                    {x.year_end && <span className="text-muted"> · {x.year_end}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Highlights */}
          {parsed.highlights?.length > 0 && (
            <div className="rounded-xl border p-3" style={{ borderColor: "var(--rule)" }} data-testid="ai-highlights">
              <div className="text-[11px] uppercase tracking-wider font-bold text-muted mb-2 flex items-center gap-1">
                <Award size={12} /> Highlights
              </div>
              <ul className="text-sm space-y-1 list-disc pl-5">
                {parsed.highlights.slice(0, 6).map((h, i) => <li key={`hl-${i}-${(h || "").slice(0, 40)}`}>{h}</li>)}
              </ul>
            </div>
          )}

          {data?.created_at && (
            <div className="text-[10px] text-muted text-center inline-flex items-center justify-center gap-1 w-full">
              <FileText size={10} /> Parsed {new Date(data.created_at).toLocaleString()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
